import { previewStaticPaths } from 'scripts/preview-routes.cjs'
import { GetStaticPaths, GetStaticProps } from 'next'
import Head from 'next/head'
import { ParsedUrlQuery } from 'querystring'
import Container from '@mui/material/Container'
import { Article, WithContext } from 'schema-dts'
import { JsonLd } from 'react-schemaorg'

import MagazineArticle from 'src/components/Magazine/MagazineArticle'
import { post } from 'src/functions/fetch'
import magazineUrl from 'src/functions/magazineUrl'
import { saveUrl } from 'src/functions/regex'
import config from 'src/config'
import shopInfos from 'template/infos'
import StaticBreadcrumbs from 'src/components/Breadcrumbs/StaticBreadcrumbs'
import {
  isPublicMagazineResponse,
  isPublicMagazinesResponse,
  publicMagazineResponse,
} from 'src/shared/apiBackendResponses/magazine'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'
import { fetchSiteShopSettings } from 'src/pageBuilder/sitePageBuilder'

interface Props {
  magazine: publicMagazineResponse
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const siteBaseUrl = (config.siteURL || shopInfos.url || '').replace(/\/$/, '')

const stripHtml = (value?: string) =>
  String(value || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const normalizeImageUrl = (value?: string) => {
  const trimmed = String(value || '').trim()
  if (!trimmed) return undefined
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `${siteBaseUrl}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`
}

const getMagazineDescription = (magazine: publicMagazineResponse) =>
  stripHtml(magazine.excerpt || magazine.subtitle || magazine.pages?.find(page => page.body)?.body)
    .slice(0, 300)

const getArticleBody = (magazine: publicMagazineResponse) =>
  stripHtml((magazine.pages || [])
    .flatMap(page => [page.kicker, page.title, page.subtitle, page.body, page.quote])
    .filter(Boolean)
    .join(' '))

const getMagazineFaqItems = (magazine: publicMagazineResponse) => {
  if ((magazine.mode || 'magazine') !== 'faq') return []

  const seen = new Set<string>()

  return (magazine.pages || [])
    .map(page => ({
      question: (page.title || page.kicker || magazine.title || '').trim(),
      answer: (page.body || page.subtitle || '').trim(),
    }))
    .filter(item => {
      const key = `${item.question.toLocaleLowerCase()}::${item.answer.toLocaleLowerCase()}`
      if (!item.question || !item.answer || seen.has(key)) return false
      seen.add(key)
      return true
    })
}

const MagazinePage: React.FC<Props> = ({ magazine, landingPageImageAiGenerated }) => {
  const title = magazine.issue
    ? `${magazine.title} | ${magazine.issue}`
    : magazine.title
  const canonicalUrl = `${siteBaseUrl}${magazine.href || magazineUrl(magazine.id, magazine.slug)}`
  const description = getMagazineDescription(magazine)
  const articleBody = getArticleBody(magazine)
  const articleImage = normalizeImageUrl(magazine.coverImage?.src)
  const articleDate = magazine.onlineFrom || magazine.created
  const faqItems = getMagazineFaqItems(magazine)
  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': magazine.mode === 'faq' ? 'FAQPage' : 'Article',
    headline: title,
    name: title,
    description: description || undefined,
    image: articleImage ? [articleImage] : undefined,
    datePublished: articleDate,
    dateModified: magazine.changed || articleDate,
    author: {
      '@type': 'Organization',
      name: shopInfos.legalname || shopInfos.name,
      url: shopInfos.url,
    },
    publisher: {
      '@type': 'Organization',
      name: shopInfos.legalname || shopInfos.name,
      url: shopInfos.url,
    },
    mainEntityOfPage: canonicalUrl,
    articleSection: magazine.tags?.join(', ') || undefined,
    articleBody: articleBody || undefined,
  } as WithContext<Article>
  const faqJsonLd = faqItems.length > 0
    ? {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqItems.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      })),
    }
    : null

  return <>
    <Head>
      <title>{title}</title>
      {description &&
        <meta name='description' content={description} />
      }
      <link rel='canonical' href={canonicalUrl} />
      {faqJsonLd &&
        <script
          type='application/ld+json'
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />
      }
    </Head>
    {magazine.mode !== 'faq' && <JsonLd<Article> item={articleJsonLd} />}
    <Container maxWidth='xl' sx={{ pt: 2 }}>
      <StaticBreadcrumbs
        items={[
          { title: 'Magazin', href: '/magazin' },
          { title: magazine.title },
        ]}
      />
    </Container>
    <MagazineArticle magazine={magazine} landingPageImageAiGenerated={landingPageImageAiGenerated} />
  </>
}

export default MagazinePage

export const getStaticPaths: GetStaticPaths = async () => {
  const preview = previewStaticPaths('magazine')
  if (preview) return preview

  const data = await post({
    url: 'magazines',
    body: config.shop,
    local: true,
  })

  if (!isPublicMagazinesResponse(data)) {
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  return {
    paths: data.magazines.map(magazine => ({
      params: {
        mid: String(magazine.id),
        slug: saveUrl(magazine.slug || magazine.title || String(magazine.id)),
      },
    })),
    fallback: process.env.BUILD ? false : 'blocking',
  }
}

interface IParams extends ParsedUrlQuery {
  mid: string
  slug: string
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { mid, slug } = params as IParams
  const magazineId = Number(mid)

  if (!Number.isFinite(magazineId)) return { notFound: true, revalidate: 60 }

  const data = await post({
    url: `magazines/${magazineId}`,
    body: config.shop,
    local: true,
  })

  if (!isPublicMagazineResponse(data)) return { notFound: true, revalidate: 60 }

  const canonicalSlug = saveUrl(data.slug || data.title || String(data.id))
  if (canonicalSlug !== slug) {
    return {
      redirect: {
        destination: magazineUrl(data.id, canonicalSlug),
        permanent: true,
      },
    }
  }

  const siteSettings = await fetchSiteShopSettings({ local: true }).catch(() => undefined)
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    data.landingPageImageAiGenerated,
    siteSettings?.landingPageImageAiGenerated,
  )

  return {
    props: {
      magazine: data,
      ...(landingPageImageAiGenerated
        ? { landingPageImageAiGenerated }
        : {}),
    },
    revalidate: 300,
  }
}
